import {
  CheckCircleOutline,
  Refresh,
} from "@material-ui/icons";
import Button from "components/Button";
import { HTTPError } from "ky";
import { observer } from "mobx-react-lite";
import { useProject } from "pages/Workspace/hooks";
import React, { useCallback, useEffect, useState } from "react";
import { useStore } from "store";
import styled from "styled-components";
import { Toast } from "utils";

type Props = {
  onPostUpdate?: () => Promise<void>;
  disabled?: boolean;
  // Keep the button disabled until the project's formulation sign off is completed.
  requireSignOff?: boolean;
};

const SavedText = styled.p`
  text-transform: uppercase;
  margin-bottom: 0;
  span {
    font-weight: 600;
    font-size: 0.75rem;
    color: #74b469;
  }

  svg {
    margin-right: 0.625rem;
    fill: #74b469;
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const GenerateCostSheetButton: React.FC<Props> = ({
  onPostUpdate,
  disabled,
  requireSignOff,
}) => {
  const store = useStore();
  const project = useProject();

  const [costSheetGenerated, setCostSheetGenerated] = useState(false);

  // The project's signoffStatus is refreshed after every Define save, so the
  // button disables as soon as a change flips the sign off to "resubmit".
  const resignOffRequired = project?.signoffStatus === "resubmit";
  const awaitingSignOff =
    !!requireSignOff && project?.signoffStatus !== "completed";
  const isDisabled = disabled || awaitingSignOff || resignOffRequired;

  const handleGenerateCostSheetClick = useCallback(async () => {
    if (!project) {
      return;
    }

    project.setLoading();

    try {
      await Promise.all([
        store.requirements.saveAll(project.id),
        store.formulations.saveAll(project.id),
      ]);
      await project.generateCostSheet();
      setCostSheetGenerated(true);
      await store.projects.detail(project.id);
    } catch (e) {
      const detail =
        e instanceof HTTPError && e.response.status === 400
          ? (await e.response.json().catch(() => undefined))?.detail
          : undefined;
      Toast.danger(detail ?? "Cost sheet (frame) could not be generated.");
      // The status may have changed server side; pick it up so the button disables.
      await store.projects.detail(project.id);
      return;
    } finally {
      project.setNotLoading();
    }

    await onPostUpdate?.();

    Toast.success("Cost sheet (frame) updated.");
  }, [
    onPostUpdate,
    project,
    store.formulations,
    store.projects,
    store.requirements,
  ]);

  useEffect(() => {
    if (project?.isCostSheetDirty) {
      setCostSheetGenerated(false);
    }
  }, [project?.isCostSheetDirty]);

  return (
    <>
      <Button
        variant={project?.isCostSheetDirty ? "cost-sheet-pending" : "primary"}
        size="thin"
        onClick={handleGenerateCostSheetClick}
        disabled={project?.loading || isDisabled}
        title={
          resignOffRequired
            ? "Re-signoff is required before updating the cost sheet."
            : isDisabled
            ? "Get sign off approval before updating the cost sheet."
            : undefined
        }
      >
        <Refresh /> Update Cost Sheet (Frame)
      </Button>
      {costSheetGenerated && (
        <SavedText>
          <CheckCircleOutline />
          <span>All changes have been saved.</span>
        </SavedText>
      )}
    </>
  );
};

export default observer(GenerateCostSheetButton);
